import { useMutation } from 'react-query';
import { InvoiceDetailModel, PaymentOrigin } from '@frontend/api-invoices';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { SetupFutureUsage } from '@frontend/api-weave-pay';
import {
  useConfirmStripePaymentViaPaymentMethod,
  UseCreatePaymentIntentSyncOptions,
  useInvoicePerson,
} from '../../../../../hooks';

interface UseCardOnFilePaymentOptions {
  paymentMethod?: PaymentMethod;
  invoice?: InvoiceDetailModel;
  onInvoiceAlreadyPaid: UseCreatePaymentIntentSyncOptions['onInvoiceAlreadyPaid'];
  onRefetchInvoice: UseCreatePaymentIntentSyncOptions['onRefetchInvoice'];
}

export const useCardOnFilePayment = ({
  paymentMethod,
  invoice,
  onInvoiceAlreadyPaid,
  onRefetchInvoice,
}: UseCardOnFilePaymentOptions) => {
  const locationId = invoice?.locationId;
  const personId = invoice?.person.id;
  const { personEmail } = useInvoicePerson(invoice);
  const email = personEmail;
  const amount = invoice?.billedAmount ?? 0;

  const { makePayment } = useConfirmStripePaymentViaPaymentMethod({
    amount,
    email,
    personId,
    locationId,
    origin: PaymentOrigin.ManualCardEntry,
    /**
     * Stripe defaults to onSession if unspecified. Even if the card is already on file,
     * and most cards were saved with onSession so OTP is being triggered.
     * To prevent OTP for card on file we are now passing offSession with the Backend updates
     *
     * If we need use this hook elsewhere, we can use a prop to decide this.
     */
    setupFutureUsage: SetupFutureUsage.offSession,
    paymentMethod,
    invoiceId: invoice?.id,
    onInvoiceAlreadyPaid,
    onRefetchInvoice,
  });

  const { mutateAsync: makeCardOnFilePayment, isLoading: makingCardOnFilePayment } = useMutation({
    mutationFn: () => makePayment(),
  });

  return { makeCardOnFilePayment, makingCardOnFilePayment };
};
